import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { ActivityItem, AttentionItem, DepartmentPlacementRate, KpiOverview, Student } from '../../models/college-dashboard.model';
import { CollegeDashboardService } from '../../services/college-dashboard.service';
import { CollegePortalJobsService } from '../../../college-workspace/services/college-portal-jobs.service';
import { CollegeJobListItem } from '../../../college-workspace/models/college-jobs.models';
import { parseYearOfPassing } from '../../utils/academic-year.util';

@Component({
  selector: 'app-college-overview',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './college-overview.component.html',
  styleUrl: './college-overview.component.scss'
})
export class CollegeOverviewComponent implements OnInit, OnDestroy {
  @Output() viewAllJobs = new EventEmitter<void>();
  @Output() selectJob = new EventEmitter<string>();
  @Output() selectStudent = new EventEmitter<Student>();
  @Output() openAttention = new EventEmitter<'Profiles Incomplete' | 'Offers Awaiting Response' | 'New Companies This Week'>();

  kpis: KpiOverview[] = [];
  jobs: CollegeJobListItem[] = [];
  totalJobs = 0;
  isLoadingJobs = false;
  attentionItems: AttentionItem[] = [];
  departmentRates: DepartmentPlacementRate[] = [];
  activities: ActivityItem[] = [];

  private course?: string;
  private settingsSubscription = new Subscription();

  constructor(
    private dashboardService: CollegeDashboardService,
    private jobsService: CollegePortalJobsService
  ) {}

  ngOnInit(): void {
    // KPI cards are scoped to this college's own students, so they refresh
    // whenever the shared Academic Year / Department selectors change (the
    // same settings$ the header writes to via CollegeDashboardService).
    this.settingsSubscription.add(
      this.dashboardService.settings$.subscribe((settings) => {
        this.course = settings.department !== 'All Departments' ? settings.department : undefined;
        this.loadKpis(settings.academicYear, settings.department);
        this.loadActiveOpportunities();
      })
    );
    this.attentionItems = this.dashboardService.getDynamicAttentionItems();
    this.departmentRates = this.dashboardService.getDepartmentRates();
    this.activities = this.dashboardService.getRecentActivities();
  }

  ngOnDestroy(): void {
    this.settingsSubscription.unsubscribe();
  }

  private loadKpis(academicYear: string, department: string): void {
    const yearOfPassing = parseYearOfPassing(academicYear);
    const course = department !== 'All Departments' ? department : undefined;

    this.jobsService.getStats({ yearOfPassing, course }).subscribe({
      next: (res) => {
        if (res.Success && res.Data) {
          const s = res.Data;
          // Not currently shown on the Students Applied / Assessments Taken /
          // Interviews Taken cards — see the blank `note` values below.
          // const pctOfStudents = (value: number) =>
          //   s.total_students > 0 ? `${Math.round((value / s.total_students) * 100)}% of total students` : '—';

          this.kpis = [
            { label: 'Total Students', value: s.total_students.toLocaleString(), note: 'Registered students', iconType: 'users', tone: 'lilac' },
            { label: 'Active Jobs', value: s.active_drives.toLocaleString(), note: 'Currently active', iconType: 'check', tone: 'mint' },
            { label: 'Students Applied', value: s.students_applied.toLocaleString(), note: '', iconType: 'briefcase', tone: 'blue' },
            { label: 'Assessments Taken', value: s.assessments_completed.toLocaleString(), note: '', iconType: 'clipboard', tone: 'peach' },
            { label: 'Interviews Taken', value: s.interviews_completed.toLocaleString(), note: '', iconType: 'calendar', tone: 'sky' },
            { label: 'Active Companies', value: s.active_companies.toLocaleString(), note: 'Based on current data', iconType: 'building', tone: 'green' }
          ];
        }
      },
      error: () => {
        // Leave KPIs blank rather than blocking the rest of the page.
      }
    });
  }

  get moreJobsCount(): number {
    return Math.max(0, this.totalJobs - this.jobs.length);
  }

  loadActiveOpportunities(): void {
    this.isLoadingJobs = true;
    this.jobsService.getJobs({ page: 1, limit: 5, course: this.course }).subscribe({
      next: (res) => {
        this.isLoadingJobs = false;
        if (res.Success && res.Data) {
          this.jobs = res.Data.items;
          this.totalJobs = res.Data.total;
        }
      },
      error: () => {
        this.isLoadingJobs = false;
      }
    });
  }

  onJobClick(job: CollegeJobListItem): void {
    this.selectJob.emit(job.job_id);
  }

  onAttentionClick(category: 'Profiles Incomplete' | 'Offers Awaiting Response' | 'New Companies This Week'): void {
    this.openAttention.emit(category);
  }

  onExportReport(): void {
    this.dashboardService.notify('Department report exported');
  }
}
